import { useState, useRef, useEffect } from "react";
import "./ReportFraud.css";

const priorityOptions = [
  { val: "low",    title: "Low",    copy: "General concern or suspicious detail that is not urgent." },
  { val: "medium", title: "Medium", copy: "Potential abuse or recurring issue that needs timely review." },
  { val: "high",   title: "High",   copy: "Active fraud, payment risk, or immediate account compromise." },
];

const heroTags = [
  "Secure review workflow",
  "Evidence-friendly form",
  "Fast fraud triage",
  "Candidate and company complaints",
];

const formTabs = [
  { id: "basics",   label: "Basics",   helper: "Who is reporting this?" },
  { id: "details",  label: "Details",  helper: "What happened and who is involved?" },
  { id: "evidence", label: "Evidence", helper: "Attach screenshots or receipts" },
  { id: "review",   label: "Review",   helper: "Check progress before submitting" },
];

const API_BASE = import.meta.env?.VITE_API_URL || "http://localhost:3000";

export default function ReportFraud() {
  const [activeTab,   setActiveTab]   = useState("basics");
  const [priority,    setPriority]    = useState("");
  const [fileNames,   setFileNames]   = useState([]);
  const [charCount,   setCharCount]   = useState(0);
  const [errors,      setErrors]      = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [ticketId,    setTicketId]    = useState("");
  const [submitError, setSubmitError] = useState("");
  const [progress,    setProgress]    = useState(0);
  const [isDragging,  setIsDragging]  = useState(false);

  // Store form values in state so they survive tab changes
  const [formValues, setFormValues] = useState({
    name: "", email: "", category: "", company: "", jobRef: "", description: "",
  });

  const nameRef      = useRef();
  const emailRef     = useRef();
  const catRef       = useRef();
  const descRef      = useRef();
  const fileRef      = useRef();
  const companyRef   = useRef();
  const jobRef       = useRef();
  const formShellRef = useRef();
  const didMountRef  = useRef(false);
  const [files, setFiles] = useState([]);

  // Sync refs → state on every input so values persist across tab switches
  const syncField = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    // Recompute progress
    setProgress(() => {
      const vals = {
        name:     field === "name"     ? value : formValues.name,
        email:    field === "email"    ? value : formValues.email,
        category: field === "category" ? value : formValues.category,
        desc:     field === "description" ? value : formValues.description,
      };
      const filled = [vals.name, vals.email, vals.category, vals.desc].filter(Boolean).length;
      return (filled / 4) * 100;
    });
  };

  // When tab mounts, populate refs from saved state
  useEffect(() => {
    if (nameRef.current)    nameRef.current.value    = formValues.name;
    if (emailRef.current)   emailRef.current.value   = formValues.email;
    if (catRef.current)     catRef.current.value     = formValues.category;
    if (descRef.current)    descRef.current.value    = formValues.description;
    if (companyRef.current) companyRef.current.value = formValues.company;
    if (jobRef.current)     jobRef.current.value     = formValues.jobRef;
    // Recompute progress from saved values whenever tab changes
    const filled = [formValues.name, formValues.email, formValues.category, formValues.description]
      .filter(Boolean).length;
    setProgress((filled / 4) * 100);
  }, [activeTab]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      document.querySelectorAll("[data-reveal]").forEach(el => el.classList.add("is-visible"));
      return;
    }
    const elements = document.querySelectorAll("[data-reveal]");
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("is-visible"); obs.unobserve(e.target); } }),
      { threshold: 0.16, rootMargin: "0px 0px -8% 0px" }
    );
    elements.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    formShellRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [activeTab]);

  // Validate a specific tab — uses formValues (state), not refs
  const validateTab = (tab) => {
    const newErrors = {};
    if (tab === "basics") {
      if (!formValues.name.trim())                                  newErrors.name  = "Full name is required.";
      if (!formValues.email.trim())                                 newErrors.email = "Email is required.";
      else if (!/\S+@\S+\.\S+/.test(formValues.email.trim()))       newErrors.email = "Enter a valid email address.";
      if (!formValues.category)                                     newErrors.cat   = "Please select a category.";
    }
    if (tab === "details") {
      if (!formValues.description.trim() || formValues.description.trim().length < 10)
        newErrors.desc = "Please describe the issue (at least 10 characters).";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Full validation across all required fields (for final submit)
  const validateAll = () => {
    const newErrors = {};
    if (!formValues.name.trim())                                    newErrors.name  = "Full name is required.";
    if (!formValues.email.trim())                                   newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formValues.email.trim()))         newErrors.email = "Enter a valid email address.";
    if (!formValues.category)                                       newErrors.cat   = "Please select a category.";
    if (!formValues.description.trim() || formValues.description.trim().length < 10)
      newErrors.desc = "Please describe the issue in detail (at least 10 characters).";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateAll()) {
      // Navigate to first tab with an error
      if (errors.name || errors.email || errors.cat) {
        setActiveTab("basics");
      } else if (errors.desc) {
        setActiveTab("details");
      }
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token") || null;

      const payload = {
        reporter_name:  formValues.name,
        reporter_email: formValues.email,
        target_type:    formValues.category || "other",
        target_id:      null,
        reason:         `${formValues.category || "other"}${priority ? ` (${priority})` : ""}`,
        details:        formValues.description,
        evidence_url:   formValues.company || formValues.jobRef || null,
        company:        formValues.company,
        job_ref:        formValues.jobRef,
        priority,
      };

      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/api/support/report-fraud`, {
        method:  "POST",
        headers,
        body:    JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Server error ${res.status}`);

      const displayTicket = data?.id
        ? `TH-${String(data.id).padStart(6, "0")}`
        : `TH-${Math.floor(100000 + Math.random() * 900000)}`;

      setTicketId(displayTicket);
      setShowSuccess(true);
    } catch (err) {
      console.error("Fraud report submission failed:", err);
      setSubmitError(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const goToTab = (tab) => { setErrors({}); setActiveTab(tab); };

  const goNext = () => {
    if (!validateTab(activeTab)) return;
    const nextIndex = formTabs.findIndex(t => t.id === activeTab) + 1;
    if (nextIndex < formTabs.length) setActiveTab(formTabs[nextIndex].id);
  };

  const goPrev = () => {
    const prevIndex = formTabs.findIndex(t => t.id === activeTab) - 1;
    if (prevIndex >= 0) setActiveTab(formTabs[prevIndex].id);
  };

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    setFiles(arr);
    setFileNames(arr.map(f => f.name));
  };

  const clearForm = () => {
    setFormValues({ name: "", email: "", category: "", company: "", jobRef: "", description: "" });
    setCharCount(0);
    setFileNames([]);
    setFiles([]);
    setPriority("");
    setProgress(0);
    setErrors({});
    setSubmitError("");
    setShowSuccess(false);
    setIsDragging(false);
    setActiveTab("basics");
  };

  const isComplete = formValues.name.trim() && formValues.email.trim() &&
    /\S+@\S+\.\S+/.test(formValues.email) && formValues.category &&
    formValues.description.trim().length >= 10;

  return (
    <div className="th-root">
      <section className="th-hero">
        <div className="th-hero-panel th-reveal">
          <div className="th-brand">
            <div className="th-brand-dot"></div>
            <span>TalentHive Trust and Safety Desk</span>
          </div>
          <h1 className="th-h1">
            Report <span className="th-orange">Fraud</span> with confidence.
          </h1>
          <p className="th-hero-subtitle th-text-lg">
            Help us protect job seekers, recruiters, and employers with a guided
            reporting flow built for urgent, sensitive cases.
          </p>
          <div className="th-hero-tags">
            {heroTags.map(tag => (
              <div className="th-hero-tag" key={tag}>
                <span className="th-tag-dot"></span>
                {tag}
              </div>
            ))}
          </div>
          <div className="th-hero-actions">
            <a className="th-hero-call" href="tel:+919876543210">Phone call</a>
            <button
              className="th-hero-form-btn"
              type="button"
              onClick={() => formShellRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              Fill a form
            </button>
          </div>
        </div>
      </section>

      <section className="th-tab-rail th-reveal" data-reveal>
        <div className="th-form-tabs">
          {formTabs.map((tab, index) => (
            <button
              key={tab.id}
              type="button"
              className={`th-form-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => goToTab(tab.id)}
            >
              <span className="th-form-tab-step">0{index + 1}</span>
              <span className="th-form-tab-copy">
                <strong>{tab.label}</strong>
                <span>{tab.helper}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <main className="th-main">
        <section className="th-main-content th-reveal" data-reveal ref={formShellRef}>
          <div className="th-right-card">
            <div className="th-section-label">
              <span className="th-section-mark">RF</span>
              Fraud report form
            </div>

            <div className="th-form-intro">
              <div>
                <h2 className="th-h2">Submit your report</h2>
                <p className="th-text-sm">
                  Complete the essentials below. Required fields are marked <span className="th-req">*</span> and your progress updates as you go.
                </p>
              </div>
              <div className="th-progress-meta">
                <div className="th-progress-copy">
                  <span>Completion</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="th-progress-bar">
                  <div className="th-progress-fill" style={{ width: `${progress}%` }} />
                </div>
              </div>
            </div>

            {submitError && (
              <div style={{
                background: "var(--red-soft)", color: "var(--red)",
                border: "1px solid rgba(216,75,57,0.3)",
                borderRadius: 12, padding: "10px 16px", marginBottom: 16, fontSize: 14,
              }}>
                {submitError}
              </div>
            )}

            <form className="th-form" onSubmit={e => e.preventDefault()}>

              {/* ── BASICS ── */}
              {activeTab === "basics" && (
                <div className="th-form-panel">
                  <div className="th-form-row">
                    <div className={`th-form-group ${errors.name ? "th-error" : ""}`}>
                      <label className="th-form-label">
                        Full name <span className="th-req">*</span>
                      </label>
                      <input
                        ref={nameRef}
                        className="th-form-input"
                        type="text"
                        placeholder="Enter your full name"
                        defaultValue={formValues.name}
                        onChange={e => syncField("name", e.target.value)}
                      />
                      {errors.name && (
                        <span style={{ color: "var(--red)", fontSize: "0.78rem" }}>{errors.name}</span>
                      )}
                      <span className="th-field-note th-text-xs">Use the name tied to your TalentHive profile if possible.</span>
                    </div>

                    <div className={`th-form-group ${errors.email ? "th-error" : ""}`}>
                      <label className="th-form-label">
                        Email address <span className="th-req">*</span>
                      </label>
                      <input
                        ref={emailRef}
                        className="th-form-input"
                        type="email"
                        placeholder="your.email@example.com"
                        defaultValue={formValues.email}
                        onChange={e => syncField("email", e.target.value)}
                      />
                      {errors.email && (
                        <span style={{ color: "var(--red)", fontSize: "0.78rem" }}>{errors.email}</span>
                      )}
                      <span className="th-field-note th-text-xs">We will send confirmation and ticket updates here.</span>
                    </div>
                  </div>

                  <div className="th-form-row">
                    <div className={`th-form-group ${errors.cat ? "th-error" : ""}`}>
                      <label className="th-form-label">
                        Issue category <span className="th-req">*</span>
                      </label>
                      <select
                        ref={catRef}
                        className="th-form-select"
                        defaultValue={formValues.category}
                        onChange={e => syncField("category", e.target.value)}
                      >
                        <option value="" disabled>Select category</option>
                        <option value="bug">Bug or technical error</option>
                        <option value="account">Account and login issues</option>
                        <option value="billing">Billing and payments</option>
                        <option value="jobs">Job listings and hiring</option>
                        <option value="candidate">Candidate safety and harassment</option>
                        <option value="company">Company conduct and abuse</option>
                        <option value="fraud">Fraudulent activity</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.cat && (
                        <span style={{ color: "var(--red)", fontSize: "0.78rem" }}>{errors.cat}</span>
                      )}
                      <span className="th-field-note th-text-xs">Choose the closest match so we can route your report correctly.</span>
                    </div>

                    <div className="th-form-group">
                      <label className="th-form-label">Priority level</label>
                      <div className="th-priority-group">
                        {priorityOptions.map(option => (
                          <button
                            key={option.val}
                            type="button"
                            className={`th-priority-btn ${priority === option.val ? `selected-${option.val}` : ""}`}
                            onClick={() => setPriority(option.val)}
                          >
                            <span className="th-priority-title">{option.title}</span>
                            <span className="th-priority-copy">{option.copy}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── DETAILS ── */}
              {activeTab === "details" && (
                <div className="th-form-panel">
                  <div className="th-form-row">
                    <div className="th-form-group">
                      <label className="th-form-label">Company or employer</label>
                      <input
                        ref={companyRef}
                        className="th-form-input"
                        type="text"
                        placeholder="Company name if the issue involves an employer"
                        defaultValue={formValues.company}
                        onChange={e => syncField("company", e.target.value)}
                      />
                      <span className="th-field-note th-text-xs">Helpful for fake jobs, harassment, or unsafe hiring communication.</span>
                    </div>

                    <div className="th-form-group">
                      <label className="th-form-label">Job title or reference ID</label>
                      <input
                        ref={jobRef}
                        className="th-form-input"
                        type="text"
                        placeholder="Job title, job ID, or complaint reference"
                        defaultValue={formValues.jobRef}
                        onChange={e => syncField("jobRef", e.target.value)}
                      />
                      <span className="th-field-note th-text-xs">Share the role title, ticket number, or any reference shared with you.</span>
                    </div>
                  </div>

                  <div className={`th-form-group th-span-2 ${errors.desc ? "th-error" : ""}`}>
                    <label className="th-form-label">
                      Describe the issue <span className="th-req">*</span>
                    </label>
                    <textarea
                      ref={descRef}
                      className="th-form-textarea"
                      placeholder="Tell us what happened, when it started, and any names, links, or messages involved."
                      maxLength={1000}
                      defaultValue={formValues.description}
                      onChange={e => {
                        setCharCount(e.target.value.length);
                        syncField("description", e.target.value);
                      }}
                    />
                    {errors.desc && (
                      <span style={{ color: "var(--red)", fontSize: "0.78rem" }}>{errors.desc}</span>
                    )}
                    <div className="th-char-count th-text-xs">{charCount}/1000 characters</div>
                  </div>
                </div>
              )}

              {/* ── EVIDENCE ── */}
              {activeTab === "evidence" && (
                <div className="th-form-panel">
                  <div className="th-form-group th-span-2">
                    <label className="th-form-label">Attachments <span className="th-field-note">(optional)</span></label>
                    <div
                      className={`th-upload-zone ${isDragging ? "dragover" : ""}`}
                      onClick={() => fileRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={e => { e.preventDefault(); setIsDragging(false); handleFiles(e.dataTransfer.files); }}
                    >
                      <input
                        ref={fileRef}
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx"
                        style={{ display: "none" }}
                        onChange={e => handleFiles(e.target.files)}
                      />
                      <div className="th-upload-zone-inner">
                        <div className="th-upload-icon">UP</div>
                        <p className="th-upload-text">Attach screenshots, invoices, or proof</p>
                        <p className="th-upload-subtext">
                          Drag and drop files here or click to browse. Supported: PNG, JPG, PDF, DOC, DOCX.
                        </p>
                        {fileNames.length > 0 && (
                          <div className="th-file-list">
                            {fileNames.map(name => (
                              <div className="th-file-item" key={name}>
                                <span>{name}</span>
                                <span className="th-file-badge">Attached</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="th-field-note th-text-xs" style={{ marginTop: 6 }}>
                      File uploads are optional — an admin will follow up if evidence is needed.
                    </span>
                  </div>
                </div>
              )}

              {/* ── REVIEW ── */}
              {activeTab === "review" && (
                <div className="th-form-panel th-review-panel">
                  {/* Validation summary */}
                  {!isComplete && (
                    <div style={{
                      background: "var(--amber-soft)", border: "1px solid rgba(213,138,20,0.3)",
                      borderRadius: 14, padding: "0.85rem 1rem", marginBottom: "1rem",
                    }}>
                      <p style={{ margin: "0 0 0.5rem", fontWeight: 700, fontSize: "0.85rem", color: "var(--amber)" }}>
                        Please complete these required fields before submitting:
                      </p>
                      <ul style={{ margin: 0, paddingLeft: "1.2rem", fontSize: "0.82rem", color: "var(--ink-soft)" }}>
                        {!formValues.name.trim()     && <li>Full name (Basics tab)</li>}
                        {!formValues.email.trim()    && <li>Email address (Basics tab)</li>}
                        {formValues.email.trim() && !/\S+@\S+\.\S+/.test(formValues.email) && (
                          <li>Valid email address (Basics tab)</li>
                        )}
                        {!formValues.category        && <li>Issue category (Basics tab)</li>}
                        {formValues.description.trim().length < 10 && <li>Issue description — at least 10 characters (Details tab)</li>}
                      </ul>
                    </div>
                  )}

                  <div className="th-review-grid">
                    <div className="th-review-card">
                      <span>Reporter</span>
                      <strong style={{ fontSize: "0.88rem" }}>{formValues.name || "—"}</strong>
                      <p>{formValues.email || "No email entered"}</p>
                    </div>
                    <div className="th-review-card">
                      <span>Category</span>
                      <strong style={{ fontSize: "0.88rem", textTransform: "capitalize" }}>
                        {formValues.category || "Not selected"}
                      </strong>
                      <p>Priority: {priority ? priority.toUpperCase() : "Not set"}</p>
                    </div>
                    <div className="th-review-card">
                      <span>Files attached</span>
                      <strong>{fileNames.length}</strong>
                      <p>Evidence helps the trust and safety team verify the timeline faster.</p>
                    </div>
                  </div>

                  {formValues.description && (
                    <div style={{
                      marginTop: "0.85rem", padding: "0.85rem", borderRadius: 14,
                      background: "rgba(255,255,255,0.84)", border: "1px solid rgba(22,22,22,0.08)",
                    }}>
                      <p style={{ margin: "0 0 0.4rem", fontSize: "0.72rem", fontWeight: 800,
                        letterSpacing: "0.08em", color: "var(--orange-deep)", textTransform: "uppercase" }}>
                        Your description
                      </p>
                      <p style={{ margin: 0, fontSize: "0.84rem", color: "var(--ink-soft)", lineHeight: 1.6 }}>
                        {formValues.description.slice(0, 200)}{formValues.description.length > 200 ? "…" : ""}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="th-form-actions">
                <button
                  type="button"
                  className="th-form-secondary"
                  onClick={goPrev}
                  disabled={activeTab === "basics"}
                >
                  Back
                </button>

                {activeTab !== "review" ? (
                  <button type="button" className="th-form-primary" onClick={goNext}>
                    Continue
                  </button>
                ) : (
                  <button
                    className="th-submit-btn"
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={!isComplete ? { opacity: 0.65 } : {}}
                  >
                    {submitting ? (
                      <>
                        <span>Submitting report</span>
                        <div className="th-loading-dots"></div>
                      </>
                    ) : (
                      <>
                        <span>Submit fraud report</span>
                        <span className="th-submit-arrow">→</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </section>
      </main>

      {showSuccess && (
        <div className="th-modal-overlay" onClick={clearForm}>
          <div className="th-modal" onClick={e => e.stopPropagation()}>
            <div className="th-success-icon">✓</div>
            <h3 className="th-modal-title">Report submitted successfully</h3>
            <p className="th-modal-description th-text-base">
              Thank you for helping keep TalentHive safe. Our team will review the details and continue the investigation from here.
            </p>
            <div className="th-ticket-id">Ticket #{ticketId}</div>
            <p className="th-modal-sub th-text-sm">
              A confirmation has been sent to your email. Use this ticket number for follow-up communication.
            </p>
            <button className="th-modal-close" onClick={clearForm}>
              Back to help center
            </button>
          </div>
        </div>
      )}
    </div>
  );
}